import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, ShieldCheck, ShieldX, ShieldAlert, LogOut, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GFS_LOGO_URL } from "@/lib/brand";
import { api } from "@/lib/api";

export default function Pending() {
  const { user, logout, refreshUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const data = await api.get("/auth/me");
        if (data?.data?.status === "Active") {
          clearInterval(interval);
          refreshUser();
          const role = data?.data?.role;
          if (role === "admin") navigate("/admin");
          else if (role === "facility") navigate("/facility");
          else navigate("/collect");
        }
      } catch (err) {
        // Silently ignore — interval will retry every 10s until status changes.
        console.debug("pending poll /auth/me failed", err);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  async function refresh() {
    setChecking(true);
    try {
      await api.get("/auth/me");
      // AuthProvider doesn't auto-refresh user — force reload to pick up new status
      window.location.reload();
    } catch {
      window.location.reload();
    }
    setChecking(false);
  }

  if (!user) return null;

  let title = "Your account is pending approval";
  let body = "The administrator will review your registration shortly. We'll let you know as soon as your account is activated.";
  let Icon = Clock;
  let tint = "#A0522D";
  let bg = "#E4D5B7";

  if (user.status === "Rejected") {
    title = "Your account was not approved";
    body = user.rejection_reason || "Please contact the administrator for details.";
    Icon = ShieldX;
    tint = "#A0522D";
    bg = "#A0522D22";
  } else if (user.status === "Suspended") {
    title = "Your account is suspended";
    body = user.rejection_reason || "Please contact the administrator to restore access.";
    Icon = ShieldAlert;
    tint = "#A0522D";
    bg = "#E4D5B7";
  } else if (user.status === "Active") {
    title = "Account active";
    body = "Welcome back! Continue to your dashboard.";
    Icon = ShieldCheck;
    tint = "#056839";
    bg = "#D2E4D6";
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4" data-testid="pending-page">
      <div className="max-w-md w-full">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={GFS_LOGO_URL} alt="GreenForest" className="w-10 h-10 object-contain" />
          <div className="font-heading text-2xl font-semibold">
            PRO<span className="text-[#056839]">cess</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#D2E4D6] p-8 shadow-sm text-center">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: bg }}
          >
            <Icon className="w-10 h-10" style={{ color: tint }} />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-[#000000]" data-testid="pending-title">
            {title}
          </h1>
          <p className="text-sm text-[#3F3F3F] mt-3 leading-relaxed" data-testid="pending-body">
            {body}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-left">
            <div className="bg-[#F9F8F6] rounded-lg px-3 py-2">
              <div className="text-[#69A488] uppercase tracking-wider text-[10px] font-medium">Account</div>
              <div className="font-medium text-[#000000] mt-0.5">{user.name}</div>
              <div className="text-[#3F3F3F]/70 truncate">{user.email}</div>
            </div>
            <div className="bg-[#F9F8F6] rounded-lg px-3 py-2">
              <div className="text-[#69A488] uppercase tracking-wider text-[10px] font-medium">Status</div>
              <div className="font-medium mt-0.5" style={{ color: tint }}>
                {user.status}
              </div>
              <div className="text-[#3F3F3F]/70 capitalize">{user.role}</div>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-2">
            <Button
              onClick={refresh}
              disabled={checking}
              data-testid="pending-refresh"
              className="gfs-bg-primary hover:bg-[#044E2A] text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${checking ? "animate-spin" : ""}`} />
              Check status again
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              data-testid="pending-logout"
              className="border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        <div className="text-center text-xs text-[#3F3F3F]/60 mt-6">
          Already approved?{" "}
          <Link to="/login" className="text-[#056839] underline">
            Sign in again
          </Link>
        </div>
      </div>
    </div>
  );
}
